
"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getAuthErrorMessage } from "@/lib/auth-errors";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AUTH_CURRENT_PASSWORD_REQUIRED, AUTH_WEAK_PASSWORD } from "@/lib/auth-copy";

const formSchema = z.object({
    currentPassword: z.string().min(1, AUTH_CURRENT_PASSWORD_REQUIRED.description),
    newPassword: z.string().min(6, AUTH_WEAK_PASSWORD.description),
});

export function UpdatePasswordForm() {
    const { reauthenticate, updateUserPassword } = useAuth();
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            currentPassword: "",
            newPassword: "",
        },
    });

    async function onSubmit(values: z.infer<typeof formSchema>) {
        setIsLoading(true);
        try {
            await reauthenticate(values.currentPassword);
            await updateUserPassword(values.newPassword);
            toast({
                title: "Password Updated",
                description: "Your password has been successfully updated.",
            });
            form.reset();
        } catch (error: any) {
            console.error("Password update error:", error);
            
            // Get user-friendly error message
            const errorMessage = getAuthErrorMessage(error, 'update');
            
            toast({
                variant: "destructive",
                title: errorMessage.title,
                description: errorMessage.description,
            });
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Update Password</CardTitle>
                <CardDescription>Change your password here. After updating, you will be logged out.</CardDescription>
            </CardHeader>
            <CardContent>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="currentPassword"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Current Password</FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Input 
                                                type={showCurrentPassword ? "text" : "password"} 
                                                {...field} 
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                                disabled={isLoading}
                                            >
                                                {showCurrentPassword ? (
                                                    <EyeOff className="h-4 w-4" />
                                                ) : (
                                                    <Eye className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </FormControl>
                                    {form.formState.errors.currentPassword && (
                                        <Alert variant="destructive" aria-live="polite">
                                            <AlertTitle>{AUTH_CURRENT_PASSWORD_REQUIRED.title}</AlertTitle>
                                            <AlertDescription>{AUTH_CURRENT_PASSWORD_REQUIRED.description}</AlertDescription>
                                        </Alert>
                                    )}
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="newPassword"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>New Password</FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Input 
                                                type={showNewPassword ? "text" : "password"} 
                                                {...field} 
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                disabled={isLoading}
                                            >
                                                {showNewPassword ? (
                                                    <EyeOff className="h-4 w-4" />
                                                ) : (
                                                    <Eye className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </FormControl>
                                    {form.formState.errors.newPassword && (
                                        <Alert variant="destructive" aria-live="polite">
                                            <AlertTitle>{AUTH_WEAK_PASSWORD.title}</AlertTitle>
                                            <AlertDescription>{AUTH_WEAK_PASSWORD.description}</AlertDescription>
                                        </Alert>
                                    )}
                                </FormItem>
                            )}
                        />
                        <Button type="submit" disabled={isLoading}>
                            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Update Password
                        </Button>
                    </form>
                </Form>
            </CardContent>
        </Card>
    );
}
